'use client'

import Link from 'next/link'
import MainNav from '@/app/components/MainNav'
import {
  Calculator,
  FileText,
  TrendingUp,
  MapPin,
  DollarSign,
  Shield,
  ArrowRight,
} from 'lucide-react'

/* ── Tool definitions ─────────────────────────────────── */
const tools = [
  {
    href: '/calculator',
    title: 'Pay Calculator',
    description:
      'Break down your total military compensation — base pay, BAH, BAS, special pays, and tax advantages in one view.',
    icon: Calculator,
    tag: 'Most Popular',
  },
  {
    href: '/calculator/contract',
    title: 'Contract Modeler',
    description:
      'Visualize the total value of an enlistment contract with bonuses, promotions, and cumulative earnings over time.',
    icon: FileText,
  },
  {
    href: '/calculator/transition',
    title: 'Transition Planner',
    description:
      'Map your military-to-civilian financial picture — compare your current compensation to civilian job offers.',
    icon: TrendingUp,
  },
  {
    href: '/calculator/bah',
    title: 'BAH Lookup',
    description:
      'Compare Basic Allowance for Housing rates across up to 5 ZIP codes with rank-by-rank tables and charts.',
    icon: MapPin,
  },
  {
    href: '/calculator/tsp',
    title: 'TSP Projector',
    description:
      'Forecast your Thrift Savings Plan growth with fund allocation, BRS matching, and compound interest projections.',
    icon: DollarSign,
  },
  {
    href: '/calculator/deployment',
    title: 'Deployment Savings',
    description:
      'Calculate how much you can save on a combat deployment — tax-free pay, special pays, SDP, and reduced expenses.',
    icon: Shield,
  },
] as const

/* ── Tool card ────────────────────────────────────────── */
function ToolCard({ tool }: { tool: typeof tools[number] }) {
  return (
    <Link
      href={tool.href}
      className="group relative flex flex-col rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm ring-2 ring-transparent transition-all hover:shadow-md hover:-translate-y-0.5 group-hover:ring-blue-500/30"
    >
      {/* Tag */}
      {'tag' in tool && tool.tag && (
        <span className="absolute right-4 top-4 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-500">
          {tool.tag}
        </span>
      )}

      {/* Icon */}
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10">
        <tool.icon className="h-6 w-6 text-[var(--accent)]" />
      </div>

      {/* Content */}
      <h2 className="text-lg font-bold text-[var(--text-1)]">{tool.title}</h2>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-[var(--text-3)]">
        {tool.description}
      </p>

      {/* CTA */}
      <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)]">
        Open tool
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  )
}

/* ── Page ──────────────────────────────────────────────── */
export default function ToolsLandingPage() {
  return (
    <div className="space-y-8 pb-16">
      <MainNav />
      <div className="mx-auto max-w-5xl px-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-[var(--text-1)] sm:text-4xl">
            Financial Tools
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-[var(--text-3)]">
            Free calculators built specifically for service members. Model your pay, plan your
            retirement, compare duty stations, and maximize every dollar of your military career.
          </p>
        </div>

        {/* Grid */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((tool) => (
            <ToolCard key={tool.href} tool={tool} />
          ))}
        </div>
      </div>
    </div>
  )
}
